import React, { useEffect } from 'react'
import { message, Modal } from 'antd'
import { useRoutes } from 'react-router-dom'
import routes from './Routes/routes'
import useAxiosInterceptor, {
  axiosInstance
} from './interceptor/axios_interceptor'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from './redux/redux_store'
import {
  startTokenRefreshTimer,
  stopTokenRefreshTimer
} from './Utility/AuthUtility/tokenRefresh.util'
import { useNavigate } from 'react-router-dom' // added navigate hook
declare global {
  interface Window {
    eruda: any
  }
}

const App: React.FC = () => {
  // useEffect(() => {
  //   const script = document.createElement("script");
  //   script.src = "https://cdn.jsdelivr.net/npm/eruda";
  //   script.onload = () => window.eruda.init();
  //   document.body.appendChild(script);
  // }, []);

  const dispatch = useDispatch()
  const navigate = useNavigate()

  // initialize axios interceptor with redux dispatch and router navigate
  useAxiosInterceptor(dispatch, navigate)

  const userActive = useSelector((state: RootState) => state.user.UserActive)

  // Initialize refresh timer if user is already logged in (e.g., after page refresh)
  useEffect(() => {
    if (userActive) {
      console.log('[APP] User already authenticated, starting refresh timer')
      startTokenRefreshTimer()
    } else {
      console.log('[APP] User not authenticated, stopping refresh timer')
      stopTokenRefreshTimer()
    }

    // Cleanup on unmount
    return () => {
      stopTokenRefreshTimer()
    }
  }, [userActive])

  useEffect(() => {
    let offlineModal: any = null

    const handleOnline = () => {
      console.log('App detected: ONLINE')
      message.success('Internet connection restored.')
      if (offlineModal) {
        offlineModal.destroy()
        offlineModal = null
      }
    }

    const handleOffline = () => {
      console.log('App detected: OFFLINE')
      if (!offlineModal) {
        offlineModal = Modal.warning({
          title: 'No Internet Connection',
          content:
            'You are currently offline. Please check your network connection. The app will reconnect automatically when the internet is restored.',
          keyboard: false, // Prevent closing with escape
          closable: false, // Hide the X button
          okButtonProps: { style: { display: 'none' } } // Hide OK button so it's purely informational and blocking
        })
      }
    }

    let failedPings = 0
    const MAX_FAILURES = 3

    // Robust layered check: ping our own backend to detect if it's reachable
    const checkConnection = async () => {
      if (!navigator.onLine) return false
      try {
        // Use a lightweight HEAD request. X-Hide-Loader prevents the loading spinner.
        await axiosInstance.head('/api/health-ping', {
          timeout: 3000,
          headers: { 'X-Hide-Loader': 'true' }
        })
        return true
      } catch (err: any) {
        // If we got an HTTP response (even 401 or 404), we reached the server
        if (
          err.response &&
          err.response.status !== 502 &&
          err.response.status !== 504
        ) {
          return true
        }
        // Network error (timeout, connection refused, dns failed, etc)
        return false
      }
    }

    // Polling fallback that requires consecutive failures before marking offline
    const interval = setInterval(async () => {
      const isActuallyOnline = await checkConnection()

      if (isActuallyOnline) {
        failedPings = 0
        if (offlineModal) {
          handleOnline()
        }
      } else {
        failedPings++
        if (failedPings >= MAX_FAILURES && !offlineModal) {
          handleOffline()
        }
      }
    }, 30000) // check every 30s

    // When browser says online, immediately verify with our backend
    const handleBrowserOnlineEvent = async () => {
      const isActuallyOnline = await checkConnection()
      if (isActuallyOnline) {
        failedPings = 0
        handleOnline()
      }
    }

    const handleBrowserOfflineEvent = () => {
      // Browser guarantees hardware disconnect, so we can fail fast
      failedPings = MAX_FAILURES
      handleOffline()
    }

    window.addEventListener('online', handleBrowserOnlineEvent)
    window.addEventListener('offline', handleBrowserOfflineEvent)

    return () => {
      window.removeEventListener('online', handleBrowserOnlineEvent)
      window.removeEventListener('offline', handleBrowserOfflineEvent)
      clearInterval(interval)
      if (offlineModal) {
        offlineModal.destroy()
      }
    }
  }, [])

  const content = useRoutes(routes)
  return content
}

export default App
